import { useEffect, useState } from "react";

// Обои переписки — фон под сообщениями открытого диалога. Выбираются в
// настройках и запоминаются в этом браузере, как тема.
//
// Рисуются цветом текущей темы: узоры — SVG, собранные здесь же с её акцентом,
// подсветки — градиенты. Поэтому при смене темы обои перекрашиваются сами
// (useChatWallpaperStyle следит за data-theme на <html>), а не остаются
// индиговыми на бордовой панели.
export const CHAT_WALLPAPERS = [
  { id: "none", label: "Без обоев" },
  { id: "waves", label: "Волны" },
  { id: "hex", label: "Соты" },
  { id: "topo", label: "Топография" },
  { id: "stars", label: "Звёзды" },
  { id: "stripes", label: "Полосы" },
  { id: "rain", label: "Дождь" },
  { id: "glow", label: "Сияние" }
];

const STORAGE_KEY = "kl-chat-wallpaper";
const CHANGE_EVENT = "umbrella:chat-wallpaper";
const KNOWN = new Set(CHAT_WALLPAPERS.map((item) => item.id));

export const readChatWallpaper = () => {
  try {
    const raw = String(window.localStorage.getItem(STORAGE_KEY) || "").trim();
    return KNOWN.has(raw) ? raw : "none";
  } catch {
    return "none";
  }
};

export const saveChatWallpaper = (id) => {
  const next = KNOWN.has(id) ? id : "none";
  try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* приватное окно */ }
  try { window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next })); } catch { /* старый браузер */ }
  return next;
};

// Узоры детерминированы: одна и та же «случайность» на каждом открытии, иначе
// звёзды и капли перескакивали бы при каждой перерисовке.
const rng = (seed) => {
  let value = seed;
  return () => {
    value = (value * 9301 + 49297) % 233280;
    return value / 233280;
  };
};
const svg = (w, h, body) => `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${body}</svg>`;
const svgUrl = (markup) => `url("data:image/svg+xml;utf8,${encodeURIComponent(markup)}")`;

const wavesTile = (rgb) => {
  const W = 400;
  const rows = [];
  for (let i = 0; i < 9; i += 1) {
    const y0 = 14 + i * 28;
    const amp = 5 + (i % 3) * 2.5;
    const phase = i * 0.9;
    let d = "";
    for (let x = 0; x <= W; x += 8) {
      const y = y0 + amp * Math.sin((x / W) * Math.PI * 4 + phase);
      d += `${x === 0 ? "M" : "L"}${x} ${y.toFixed(1)}`;
    }
    rows.push(`<path d="${d}" stroke="rgba(${rgb},${(0.07 + (i % 2) * 0.04).toFixed(2)})"/>`);
  }
  return { markup: svg(W, 252, `<g fill="none" stroke-width="1.2">${rows.join("")}</g>`), size: "400px 252px" };
};

// Шестиугольники «остриём вверх»: шаг √3·s по горизонтали и 1.5·s по
// вертикали. Второй ряд выходит за нижний край плитки, поэтому его
// продолжение нарисовано и сверху.
const hexTile = (rgb) => {
  const s = 18;
  const w = Math.sqrt(3) * s;
  const h = 3 * s;
  const hex = (cx, cy) => {
    const pts = [];
    for (let k = 0; k < 6; k += 1) {
      const a = Math.PI / 6 + (k * Math.PI) / 3;
      pts.push(`${(cx + s * Math.cos(a)).toFixed(2)} ${(cy + s * Math.sin(a)).toFixed(2)}`);
    }
    return `<path d="M${pts.join("L")}Z"/>`;
  };
  const body = [hex(w / 2, s), hex(0, 2.5 * s), hex(w, 2.5 * s), hex(0, -0.5 * s), hex(w, -0.5 * s), hex(w / 2, 4 * s)].join("");
  return {
    markup: svg(w.toFixed(2), h, `<g fill="none" stroke="rgba(${rgb},0.10)" stroke-width="1">${body}</g>`),
    size: `${w.toFixed(2)}px ${h}px`
  };
};

// Линии высот, как на карте местности: неровные кольца вокруг нескольких
// вершин, каждое четвёртое чуть ярче.
const topoTile = (rgb) => {
  const r = rng(11);
  const out = [];
  const centers = [[180, 220], [640, 160], [470, 560], [120, 720], [800, 760]];
  centers.forEach(([cx, cy]) => {
    const p1 = r() * 6;
    const p2 = r() * 6;
    const rings = 6 + Math.floor(r() * 4);
    for (let k = 1; k <= rings; k += 1) {
      const R = k * 22;
      let d = "";
      for (let a = 0; a <= 360; a += 6) {
        const t = (a * Math.PI) / 180;
        const rr = R * (1 + 0.16 * Math.sin(3 * t + p1) + 0.07 * Math.sin(5 * t + p2 + k * 0.3));
        d += `${a === 0 ? "M" : "L"}${(cx + rr * Math.cos(t)).toFixed(1)} ${(cy + rr * Math.sin(t)).toFixed(1)}`;
      }
      out.push(`<path d="${d}Z" stroke="rgba(${rgb},${k % 4 === 0 ? "0.16" : "0.08"})"/>`);
    }
  });
  return { markup: svg(900, 900, `<g fill="none" stroke-width="1.1">${out.join("")}</g>`), size: "900px 900px" };
};

// На светлом фоне белых точек не видно — там все звёзды цветом темы.
const starsTile = (rgb, light) => {
  const r = rng(3);
  const out = [];
  for (let i = 0; i < 70; i += 1) {
    const x = r() * 360;
    const y = r() * 360;
    const radius = 0.5 + r() * 1.1;
    const a = 0.12 + r() * 0.4;
    const colored = light || r() > 0.7;
    const fill = colored ? `rgba(${rgb},${a.toFixed(2)})` : `rgba(255,255,255,${(a * 0.7).toFixed(2)})`;
    out.push(`<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${radius.toFixed(2)}" fill="${fill}"/>`);
  }
  for (let i = 0; i < 5; i += 1) {
    const x = r() * 360;
    const y = r() * 360;
    const s = 3 + r() * 3;
    out.push(`<path transform="translate(${x.toFixed(1)} ${y.toFixed(1)})" d="M0 ${-s}L${s * 0.22} ${-s * 0.22}L${s} 0L${s * 0.22} ${s * 0.22}L0 ${s}L${-s * 0.22} ${s * 0.22}L${-s} 0L${-s * 0.22} ${-s * 0.22}Z" fill="rgba(${rgb},0.35)"/>`);
  }
  return { markup: svg(360, 360, out.join("")), size: "360px 360px" };
};

// Редкий дождь: короткие косые штрихи разной длины и яркости.
const rainTile = (rgb) => {
  const r = rng(9);
  const out = [];
  for (let i = 0; i < 34; i += 1) {
    const x = r() * 460;
    const y = r() * 460;
    const len = 8 + r() * 16;
    const a = (0.05 + r() * 0.08).toFixed(3);
    out.push(`<path d="M${x.toFixed(1)} ${y.toFixed(1)}l-${(len * 0.27).toFixed(1)} ${len.toFixed(1)}" stroke="rgba(${rgb},${a})"/>`);
  }
  return { markup: svg(460, 460, `<g fill="none" stroke-width="1.5" stroke-linecap="round">${out.join("")}</g>`), size: "460px 460px" };
};

const VIOLET = "139,92,246";

// Стиль фона для выбранных обоев. rgb — акцент темы («108, 115, 244»), light —
// светлая тема, fade — цвет, в который уходят края (фон под перепиской).
// null — без обоев.
export const buildChatWallpaperStyle = (id, { rgb = "108,115,244", light = false, fade = "transparent" } = {}) => {
  const c = String(rgb || "108,115,244").replace(/\s+/g, "");
  const tile = (made, layers = []) => ({
    backgroundImage: [...layers, svgUrl(made.markup)].join(", "),
    backgroundSize: [...layers.map(() => "auto"), made.size].join(", ")
  });
  switch (id) {
    case "waves":
      return tile(wavesTile(c), [`radial-gradient(800px 500px at 100% 100%, rgba(${c},0.12), transparent 70%)`]);
    case "hex":
      return tile(hexTile(c), [`radial-gradient(ellipse 110% 85% at 50% 35%, transparent 30%, ${fade} 100%)`]);
    case "topo":
      return tile(topoTile(c), [`radial-gradient(700px 480px at 0% 0%, rgba(${c},0.12), transparent 70%)`]);
    case "stars":
      return tile(starsTile(c, light), [
        `radial-gradient(900px 520px at 80% -10%, rgba(${c},0.20), transparent 70%)`,
        `radial-gradient(700px 500px at 0% 110%, rgba(${VIOLET},0.12), transparent 70%)`
      ]);
    case "stripes":
      return {
        backgroundImage: `linear-gradient(180deg, ${fade} 0%, transparent 45%), repeating-linear-gradient(-58deg, transparent 0 120px, rgba(${c},0.07) 120px 210px)`,
        backgroundSize: "auto, auto"
      };
    case "rain":
      return tile(rainTile(c), [`radial-gradient(800px 460px at 50% -10%, rgba(${c},0.14), transparent 70%)`]);
    case "glow":
      // Оба пятна — цветом темы: второе, фиолетовое, на любой теме кроме
      // индиговой выглядело чужим (на белой — серое сверху и фиолет снизу).
      return {
        backgroundImage: `radial-gradient(760px 520px at 100% 0%, rgba(${c},0.24), transparent 68%), radial-gradient(640px 520px at 0% 100%, rgba(${c},0.14), transparent 70%)`,
        backgroundSize: "auto, auto"
      };
    default:
      return null;
  }
};

const readThemeSnapshot = () => {
  if (typeof document === "undefined") return { rgb: "108,115,244", light: false, theme: "" };
  const root = document.documentElement;
  const theme = String(root.getAttribute("data-theme") || "").trim();
  const rgb = getComputedStyle(root).getPropertyValue("--accent-500-rgb").trim() || "108,115,244";
  return { rgb, light: theme === "light", theme };
};

// Цвет, на котором лежит переписка: первый непрозрачный фон вверх по дереву.
// В него уходят края у «Сот» и «Полос» — иначе на графите и в светлой теме
// они темнели бы чёрным пятном.
const resolveBackdropColor = (element) => {
  let node = element?.parentElement || null;
  while (node && node !== document.documentElement) {
    const color = getComputedStyle(node).backgroundColor;
    if (color && color !== "transparent" && !/rgba\([^)]*,\s*0\)$/.test(color)) return color;
    node = node.parentElement;
  }
  return getComputedStyle(document.body).backgroundColor || "transparent";
};

// Цвет и светлость текущей темы, с подпиской на её смену.
export const useWallpaperTheme = () => {
  const [theme, setTheme] = useState(() => readThemeSnapshot());
  useEffect(() => {
    if (typeof MutationObserver !== "function") return undefined;
    const observer = new MutationObserver(() => setTheme(readThemeSnapshot()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "style", "class"] });
    return () => observer.disconnect();
  }, []);
  return theme;
};

// Выбранные обои и тема — для фона переписки. Следит и за выбором (в этой
// вкладке — событием, в соседней — storage), и за сменой темы.
export const useChatWallpaperStyle = (elementRef) => {
  const [id, setId] = useState(() => readChatWallpaper());
  const theme = useWallpaperTheme();
  const [fade, setFade] = useState("transparent");

  useEffect(() => {
    const onChange = () => setId(readChatWallpaper());
    const onStorage = (event) => { if (!event || event.key === STORAGE_KEY) onChange(); };
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const element = elementRef?.current || null;
  useEffect(() => {
    if (!element) return;
    setFade(resolveBackdropColor(element));
  }, [element, theme.theme]);

  return buildChatWallpaperStyle(id, { rgb: theme.rgb, light: theme.light, fade });
};
